/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CachedImage from './CachedImage';
import Modal from './Modal';
import { toLocal } from '../utils/dateTime';

// Import logos for light/dark mode
import logoBlack from '../assets/truvideo-logo-black.png';
import logoWhite from '../assets/truvideo-logo-white.png';

const CaptureHeader = ({ expiresAt, brandLogo, branding = {}, className = "", type = "before-capture", onSaveAndExit, token }) => {
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Theme-based logo selection - consistent with other capture components
  const currentTheme = branding.theme || 'light';
  const isDark = ['dark', 'synthwave', 'halloween', 'aqua', 'forest', 'black', 'luxury', 'dracula', 'business', 'night', 'coffee', 'dim', 'sunset'].includes(currentTheme);

  // For overlay type, always use white logo since it's over dark background
  // Use uploaded brand logo if available,
  // otherwise fallback based on overlay type and theme

  const fallbackLogo = type === 'overlay' || isDark ? logoWhite : logoBlack;

  // Format the expiry date/time
  const formattedExpiry = useMemo(() => {
    if (!expiresAt) { return null; }

    try {
      const expiryDate = new Date(expiresAt);
      const now = new Date();

      // Check if the date is valid
      if (isNaN(expiryDate.getTime())) { return null; }

      // Check if already expired
      if (expiryDate <= now) { return 'Expired'; }

      // Format the date and time
      const options = {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };

      // Add year if it's different from current year
      if (expiryDate.getFullYear() !== now.getFullYear()) {
        options.year = 'numeric';
      }

      return toLocal(expiryDate, options);
    } catch (error) {
      console.error('Error formatting expiry date:', error);
      return null;
    }
  }, [expiresAt]);

  const handleConfirmExit = () => {
    setShowExitConfirm(false);
    onSaveAndExit?.();
  };

  return (
    <>
      <div className={`w-full flex justify-between items-center space-remove-landscap ${type === 'before-capture' || type === 'done' ? 'px-3' : ''} pt-3 pb-4 ${className}`}>
        {brandLogo ? (
        <CachedImage
          token={token}
          src={brandLogo}
          alt="TruVideo"
          className="w-auto h-[25px] object-contain"
          onFallbackFailed={(e) => { e.target.src = fallbackLogo; }}
        />
        ) : (
          <img src={fallbackLogo} alt="TruVideo" className="w-auto h-[25px] object-contain" />
        )}
        {formattedExpiry && (
          <div className="flex items-center gap-2">
            {formattedExpiry && (
              <>
                <span className={`text-[13px] font-medium ${type === 'overlay'
                  ? 'text-white drop-shadow-md'
                  : 'text-base-content/70'
                  }`}>
                  Expires:
                </span>
                <div className={`rounded-full py-[6px] px-[10px] font-bold text-[10px] tabular-nums shadow-sm leading-none ${type === 'overlay'
                  ? 'bg-[#FFFBE4] border border-[#A65200] text-[#A65200]'
                  : formattedExpiry === 'Expired'
                    ? 'bg-error/10 border border-error/30 text-error'
                    : 'bg-[#EDFFEE] border border-[#008514] text-[#008514]'
                  }`}>
                  {formattedExpiry}
                </div>
              </>
            )}
          </div>
        )}
        {onSaveAndExit && (
          <button
            type="button"
            onClick={() => setShowExitConfirm(true)}
            className="text-[11px] font-semibold btn btn-primary rounded-xl h-auto min-h-4 py-1.5 px-3"
          >
            Exit
          </button>
        )}
      </div>

      <Modal isOpen={showExitConfirm} onClose={() => setShowExitConfirm(false)} title="Save & Exit?" theme={currentTheme}>
        <div className="space-y-4">
          <p className="text-base-content/70 text-[15px]">
            Are you sure you want to save your progress and exit? You can pick up where you left off later.
          </p>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowExitConfirm(false)}
              className="flex-1 py-3 px-4 bg-base-200 text-base-content font-semibold text-[15px] rounded-xl hover:bg-base-300 transition-colors"
            >
              No
            </button>
            <button
              onClick={handleConfirmExit}
              className="flex-1 py-3 px-4 btn btn-primary font-semibold text-[15px] rounded-xl transition-colors"
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CaptureHeader;

CaptureHeader.propTypes = {
  expiresAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  brandLogo: PropTypes.string,
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  className: PropTypes.string,
  type: PropTypes.string,
  token: PropTypes.string,
  onSaveAndExit: PropTypes.func,
};
